import React, { useState } from 'react';
import { Modal, Form, Input, Button, Row, Col, Divider, Collapse } from 'antd';
import { useFetch } from '../../../../../../hooks/useFetch';
import XMLViewer from 'react-xml-viewer';
import ReactJson from 'react-json-view';
import { toast } from 'react-toastify';
import {
    LoadingOutlined,
} from '@ant-design/icons';
import { parseXmlToFormData } from '../../../../../../hooks/parseKms';
import { PostAPI } from '../../../../../../redux';
import { useDispatch } from 'react-redux';
import VerifyResend from "../../../../../../components/pages/VerifyResend";
import FormJsonModal from "./formJson";

const { Panel } = Collapse;

const tryParseJson = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed[0] !== '{' && trimmed[0] !== '[') return null;
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        return null;
    }
};

const isXml = (value) => {
    if (typeof value !== 'string') return false;
    return value.trim().startsWith('<');
};

// react-xml-viewer butuh 1 root element. Payload TPS (<HEADER>...</HEADER><DETIL>...</DETIL>) punya 2 root -> bungkus <DATA>.
const normalizeXml = (value) => {
    let trimmed = value.trim().replace(/^\s*<\?xml[^?]*\?>\s*/, '');
    if (!trimmed.startsWith('<')) return trimmed;
    const open = trimmed.match(/^<([A-Za-z_][\w:.-]*)\b[^>]*?(\/?)>/);
    if (!open) return trimmed;
    const tag = open[1];
    if (open[2] === '/') {
        if (trimmed.slice(open[0].length).trim()) return `<DATA>${trimmed}</DATA>`;
        return trimmed;
    }
    const re = new RegExp(`<(/?)${tag}\\b[^>]*?>`, 'g');
    let depth = 0, end = -1, m;
    while ((m = re.exec(trimmed))) {
        if (/\/>$/.test(m[0])) continue;
        if (m[1] !== '/') depth++;
        else if (--depth === 0) { end = m.index + m[0].length; break; }
    }
    if (end === -1) return trimmed;
    if (trimmed.slice(end).trim()) return `<DATA>${trimmed}</DATA>`;
    return trimmed;
};

const getReffNumber = (request) => {
    if (request == null) return undefined;
    const json = tryParseJson(request);
    if (json !== null) {
        return json?.REF_NUMBER || json?.HEADER?.REF_NUMBER || json?.header?.REF_NUMBER || json?.header?.refNumber || json?.refNumber || json?.ref_number || json?.reff_number;
    }
    if (typeof request === 'string' && request.includes('<REF_NUMBER>')) {
        return request.split('<REF_NUMBER>')[1]?.split('</REF_NUMBER>')[0];
    }
    return undefined;
};

const PayloadViewer = ({ value }) => {
    if (value == null || (typeof value === 'string' && !value.trim())) {
        return <p>No Data</p>;
    }

    const json = tryParseJson(value);
    if (json !== null) {
        return (
            <ReactJson
                src={json}
                collapsed={false}
                enableClipboard
                displayDataTypes={false}
                displayObjectSize={false}
                style={{ fontSize: 12 }}
            />
        );
    }

    if (isXml(value)) {
        return <XMLViewer xml={normalizeXml(value)} />;
    }

    return <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{String(value)}</pre>;
};

export default ({ data }) => {
    const dispatch = useDispatch();

    const { awb_id, mawb_number, hawb_number, status, reff_number } = data;
    const search = { type: status === "Gate In" ? "NODOK Export In" : "NODOK Export Out", flight_no: awb_id?.flight_number, awb: mawb_number, hawb: hawb_number }
    const [rows, loading] = useFetch(`api/v1/tps_logs/detail-log?search=${JSON.stringify(search)}`);
    const items = rows || [];

    const [visible, setVisible] = useState(false);
    const [visibleJson, setVisibleJson] = useState(false);
    const [visibleVerif, setVisibleVerif] = useState(false);
    const [resendType, setResendType] = useState('xml');
    const [formData, setFormData] = useState({});
    const [formJsonData, setFormJsonData] = useState({ header: {}, detil: [{}] });

    const normalizeJsonData = (parsed) => {
        if (Array.isArray(parsed)) return { header: {}, detil: parsed };
        if (parsed && typeof parsed === 'object' && ('header' in parsed || 'detil' in parsed || 'HEADER' in parsed || 'DETIL' in parsed)) {
            const header = parsed.header || parsed.HEADER || {};
            let detil = parsed.detil || parsed.DETIL || parsed.detail || [{}];
            if (!Array.isArray(detil)) detil = [detil];
            if (detil.length === 0) detil = [{}];
            return { header, detil };
        }
        return { header: {}, detil: [parsed || {}] };
    };

    const showModal = (item) => {
        const json = tryParseJson(item?.request);
        if (json !== null) {
            setFormJsonData(normalizeJsonData(json));
            setResendType('json');
            setVisibleJson(true);
        } else {
            setResendType('xml');
            setVisible(true);
            parseXmlToFormData(item?.request, setFormData);
        }
    };

    const handleOk = () => {
        const url = 'api/v1/tps_logs/resend_logs';
        dispatch(PostAPI({ url, data: { data: formData, type: status.includes("In") ? "Export In" : "Export Out" } })).then(resp => {
            if (!!resp?.payload) {
                toast.success("Resend berhasil!");
                setVisible(false);
            }
        })
    };

    const handleOkJson = () => {
        const url = 'api/v1/tps_logs/resend_logs';
        dispatch(PostAPI({ url, data: { data: formJsonData, type: status.includes("In") ? "Export In" : "Export Out" } })).then(resp => {
            if (!!resp?.payload) {
                toast.success("Resend berhasil!");
                setVisibleJson(false);
            }
        })
    };

    const handleProcessByType = () => {
        if (resendType === 'json') handleOkJson();
        else handleOk();
    };

    const handleCancel = () => {
        setVisible(false);
    };

    const handleCancelJson = () => {
        setVisibleJson(false);
    };

    const handleJsonSend = (values) => {
        setFormJsonData(values);
        setResendType('json');
        setVisibleVerif(true);
    };

    const handleChange = (changedValues) => {
        setFormData({ ...formData, ...changedValues });
    };

    return (
        <>
            <VerifyResend visible={visibleVerif} setVisible={setVisibleVerif} handleProcess={handleProcessByType} />
            <FormJsonModal visible={visibleJson} data={formJsonData} onCancel={handleCancelJson} onSend={handleJsonSend} />
            <Modal
                title="Kirim Ulang"
                visible={visible}
                onOk={() => setVisibleVerif(true)}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button key="submit" type="primary" onClick={() => setVisibleVerif(true)}>
                        Send
                    </Button>,
                ]}
                width={"70%"}
            >
                <Form
                    layout="vertical"
                    initialValues={formData}
                    onValuesChange={(changedValues) => handleChange(changedValues)}
                >
                    {/* HEADER Fields */}
                    <Row gutter={16}>
                        <Divider plain>HEADER</Divider>
                        <Col span={6}>
                            <Form.Item label="Kode Dokumen" name="KD_DOK">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Kode TPS" name="KD_TPS">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Nama Angkut" name="NM_ANGKUT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Flight" name="NO_VOY_FLIGHT">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Call Sign" name="CALL_SIGN">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Tanggal Tiba" name="TGL_TIBA">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Kode Gudang" name="KD_GUDANG">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider plain>DETAIL</Divider>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="No BL AWB" name="NO_BL_AWB">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Tanggal BL AWB" name="TGL_BL_AWB">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Master BL AWB" name="NO_MASTER_BL_AWB">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Tanggal Master BL AWB" name="TGL_MASTER_BL_AWB">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="ID Consignee" name="ID_CONSIGNEE">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Consignee" name="CONSIGNEE">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Bruto" name="BRUTO">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Bc11" name="NO_BC11">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Tgl Bc11" name="TGL_BC11">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Pos Bc11" name="NO_POS_BC11">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Const Asal" name="CONT_ASAL">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Seri Kemas" name="SERI_KEMAS">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Kode Kemas" name="KD_KEMAS">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Jumlah Kemas" name="JML_KEMAS">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Kode Timbun" name="KD_TIMBUN">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Kode Dok Inout" name="KD_DOK_INOUT">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="No Dok Inout" name="NO_DOK_INOUT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Tanggal Dok Inout" name="TGL_DOK_INOUT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Waktu Inout" name="WK_INOUT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Kode Sar Angkut Inout" name="KD_SAR_ANGKUT_INOUT">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="No Pol" name="NO_POL">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Pel Muat" name="PEL_MUAT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Pel Transit" name="PEL_TRANSIT">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Pel Bongkar" name="PEL_BONGKAR">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Pel Bongkar" name="PEL_BONGKAR">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Pel Tujuan" name="GUDANG_TUJUAN">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Pel Kantor" name="KODE_KANTOR">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No PEB" name="NO_DAFTAR_PABEAN">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Tgl PEB" name="TGL_DAFTAR_PABEAN">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Segel Bc" name="NO_SEGEL_BC">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Tgl Segel Bc" name="TGL_SEGEL_BC">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="No Ijin Tps" name="NO_IJIN_TPS">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="Tgl Ijin Tps" name="TGL_IJIN_TPS">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            <div style={{ display: 'flex', flexDirection: 'column', padding: 32 }}>
                {loading ?
                    <div>
                        <LoadingOutlined style={{ fontSize: 20 }} />
                    </div> :
                    <div>
                        <Collapse style={{ width: "80%" }}>
                            {items?.map((item, i) => {


                                const reff = getReffNumber(item?.request);
                                const lastreff = reff === reff_number ? `last send` : "";

                                return (
                                    <Panel header={`Logs ${i + 1} ${lastreff}`} key={i + 1} style={{ backgroundColor: reff === reff_number ? `#36BA98` : "" }}>
                                        <Button type="primary" onClick={() => showModal(item)} size='small'>
                                            Resend
                                        </Button>
                                        <p variant="h6">Request</p>
                                        <div style={{ display: 'flex', padding: 16, flexDirection: 'column', width: '60vw', overflow: 'scroll', border: 'solid 1px #efefef', marginBlock: 16 }}>
                                            {item?.request ?
                                                <PayloadViewer value={item?.request} /> :
                                                <p>No Request</p>
                                            }
                                        </div>
                                        <p variant="h6">Response</p>
                                        <div style={{ display: 'flex', padding: 16, flexDirection: 'column', width: '60vw', overflow: 'scroll', border: 'solid 1px #efefef', marginBlock: 16 }}>
                                            {item?.response ?
                                                <PayloadViewer value={item?.response} /> :
                                                <p>No Response</p>
                                            }
                                        </div>
                                    </Panel>
                                )
                            })}
                        </Collapse>
                    </div>
                }
            </div>
        </>
    )
}